import { waterfall, headshotSnow, wetDog, pianoChina } from './images';

export const metadata = {
  slug: "who-are-you",
  title: "who are you",
  date: "july 27, 2025",
  time_date: "2025-07-27",
  excerpt: "i talk abt me.",
  published: true
};

export const content = `I am John Kim. My Korean name is Dongwhi Kim, but no one calls me that except my parents and relatives. 

<img src="${headshotSnow}" alt="Me in the snow" style="width: 100%; max-width: 600px; height: 300px; object-fit: cover; object-position: center;" />

## Who are you (professionally)?

I am a senior at the University of Notre Dame, double majoring in CS and applied math. I am thankful to research closely with both [Nuno Moniz](https://www.nunomoniz.co/) and [Meng Jiang](http://www.meng-jiang.com/) on **trustworthy and interpretable AI**, fairness, imbalanced data, graph neural networks, and machine unlearning. Previously, I've founded a venture-backed startup (Atti), interned at AWS building AI agents, and researched trustworthy AI. I graduate in 2026, so I am searching for ~~jobs~~ what I truly want to do. I am 80% sure it is a PhD, but what really drives me is learning + solving hard problems.

As an AI researcher, of course I've read the [bitter lesson](http://www.incompleteideas.net/IncIdeas/BitterLesson.html). While I agree that methods that leverage compute prevail, and that our minds are irredeemably complex, I disagree that we should entirely avoid encoding our human discoveries into our methods that progress research. In the end, we must have learned something from the process. The prototypes we build and discard are a mark of our progress. 

I am mostly curious about why machines work as they do (interpretable), and how to make them safe (trustworthy). These systems are black-box models, yet they are used by billions of people. As researchers and innovators, I believe we are responsible for the future and the technologies we create.{{ref:1}}

## Who are you (personally)? 

<img src="${waterfall}" alt="Me after hiking at a waterfall in Puerto Rico" style="width: 100%; max-width: 600px; height: 400px; object-fit: cover; object-position: center;" />

I just like learning.

Corollary: I like reading, solving problems, and building stuff.

I think that's why I gravitate to academia, because it's a lot of reading, but you're learning so much while building solutions to problems that no one else has solved.

I like cooking, dancing (i'm bad), and exploring. I think there is a child-like fascination that still remains in me when uncovering new things, like hiking to find waterfalls, or watching cave crawling videos (i'd never do that).{{ref:2}}

I also like watching kdramas, anime, and movies. My favorite kdrama is Pinocchio (or strong girl bongsoon, or when life gives you tangerines, ~~there's too many~~), favorite anime is 86 (or hxh, or mob psycho!), and movie is maybe Train to Busan. My favorite game is probably Minecraft. I love horror movies and games too.

## Where are you from?

<img src="${pianoChina}" alt="Mall in China" style="width: 100%; max-width: 600px; height: 350px; object-fit: cover; object-position: center;" />

I was born in South Korea, Seoul. I moved to the US a year later. I grew up in New Jersey until 2018. I then moved to China, Shanghai, where I spent my entire high school years. Then I moved back to the US for university. 

I consider myself Korean-American. My Korean is not as good as I want it to be. I grew up everywhere, so I don't know where my physical home is. Instead, I realized home is where your people are (family and friends). Anywhere with them is where home is.

If you ask me where I'm from, I'll say I'm from New Jersey. Sometimes, people will ask more specific questions about New Jersey, like which high school, in which I'll then explain I moved to Shanghai, but can only say around 11 words, and my biggest regret is not learning to speak Mandarin since many of my friends are Chinese.{{ref:3}}

## What is this website?
<img src="${wetDog}" alt="My dog when I was giving him a bath" style="width: 100%; max-width: 600px; height: 350px; object-fit: cover; object-position: center;" />

I made the first version of my website in my 2nd year of university (based off [joseph han's](https://josephhan.io/)). I didn't update it that much, but given that I want to start writing blog posts, sharing updates, and do stuff, I saw fit to rehaul my personal website completely.

### Okay, so why does my website look like this?

My korean name is 김동휘. 김 is my family surname. 동 means "east" in hanja, and 휘 means "star". So together, my name means "east star" which is a pretty cool name :){{ref:4}}

The theme for the website comes from my name, but I really wanted to go for a minimalistic, academic, yet unique feeling. Especially because many PhD students and professor websites are simple, with content that speaks for itself. 

I hope I nailed a mix of minimalism with flare, but I'm concerned the website looks too flashy.

The inspiration for my website comes from:
- [zen wu](https://zen-wu.social/index.html)
- [aryaman arora](https://aryaman.io/index.html)
- [alex yen](https://www.alexlwyen.com/misc/cooking/)`;
